import React, { useMemo } from 'react';
import { useParams } from 'react-router';

import isLatLong from 'validator/lib/isLatLong';

import { useRxSearchQuery, RxSearchQueryVariables, NearbyRestaurantSearchInput, useRestaurantByShortUrlQuery } from 'src/apollo/onlineOrdering';
import { useToastLocalConfigQuery } from 'src/apollo/sites';
import useTracker from 'src/lib/js/hooks/useTracker';
import { ExperimentContextProvider } from 'src/shared/components/common/ab_testing/ABTestContext';
import { DebugContextProvider } from 'src/shared/components/common/debug/DebugContext';

import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider';
import RestaurantContextProvider from 'shared/components/common/restaurant_context/RestaurantContext';
import { RestaurantRoutesContextProvider } from 'shared/components/common/restaurant_routes/RestaurantRoutesContext';
import NoMatch404 from 'shared/components/no_match_404/NoMatch404';
import { RxDataUsage } from 'shared/js/hooks/useDefaultSiteData';

import Meta from 'public/components/default_template/meta/Meta';
import { SiftMeta } from 'public/components/default_template/meta/sift/SiftMeta';
import { RestaurantProfileRestaurantType, NearbyRestaurantSearchResultRestaurantType } from 'public/components/default_template/restaurant_profile/profileUtils';
import { StructuredDataProvider } from 'public/components/seo/context';
import { TOAST_LOCAL_ORDER_PATH, TOAST_LOCAL_PATH } from 'public/js/siteUtilities';

import { resources } from 'config';

export type WrappedProfilePageProps = {
  restaurantProfileData: RestaurantProfileRestaurantType
  nearbyRxs?: NearbyRestaurantSearchResultRestaurantType[]
};

type Props = {
  respectToastLocalConfig: boolean
}

export const RestaurantProfilePageWrapper = ({ children, respectToastLocalConfig = true }: { children: ((p: WrappedProfilePageProps) => React.ReactNode) } & Props) => {
  const { slug, rxGuid }: { slug: string, rxGuid: string } = useParams();
  const tracker = useTracker();

  const { data: tlData, loading: tlLoading } = useToastLocalConfigQuery(
    { variables: { restaurantGuid: rxGuid } }
  );

  const ooClient = useOOClient();
  const { data, loading } = useRestaurantByShortUrlQuery({
    variables: { shortUrl: decodeURIComponent(slug || '') },
    client: ooClient
  });

  const variables: RxSearchQueryVariables | null = useMemo(() => {
    const rx = data?.restaurantV2ByShortUrl.__typename === 'Restaurant' ? data.restaurantV2ByShortUrl : null;
    if(!rx?.location.latitude || !rx?.location.longitude) {
      return null;
    }
    const vars: NearbyRestaurantSearchInput = {
      limit: 7,
      coordinates: {
        latitude: rx.location.latitude,
        longitude: rx.location.longitude
      },
      searchQuery: rx.cuisineType || ''
    };
    return { input: vars };
  }, [data]);

  const validLatLong = useMemo(() =>
    data?.restaurantV2ByShortUrl?.__typename === 'Restaurant'
    && data.restaurantV2ByShortUrl.location.latitude
    && data.restaurantV2ByShortUrl.location.longitude
    && isLatLong(`(${data.restaurantV2ByShortUrl.location.latitude},${data.restaurantV2ByShortUrl.location.longitude})`),
  [data?.restaurantV2ByShortUrl]);

  const { data: searchData, loading: searchLoading } = useRxSearchQuery({
    // Skip ensures presence of variables
    variables: variables!,
    skip: !validLatLong || !variables,
    client: ooClient
  });

  const nearbyRxs = useMemo(() => {
    return searchData?.nearbyRestaurantSearch.results.filter(rx => rx.restaurant.guid !== rxGuid).map(r => r.restaurant);
  }, [rxGuid, searchData?.nearbyRestaurantSearch.results]);

  if(tlLoading || loading || searchLoading) {
    return null;
  }

  const toastLocalDisabled = respectToastLocalConfig && !tlData?.toastLocalConfig?.enabled;
  if(toastLocalDisabled || !data?.restaurantV2ByShortUrl || data.restaurantV2ByShortUrl.__typename !== 'Restaurant') {
    return <NoMatch404 />;
  }

  const homePath = `${TOAST_LOCAL_PATH}`;
  const profilePath = `${TOAST_LOCAL_ORDER_PATH}/${slug}/r-${rxGuid}`;
  const accountPath = `${TOAST_LOCAL_ORDER_PATH}/${slug}/r-${rxGuid}/account`;
  const profilePathPattern = `${TOAST_LOCAL_ORDER_PATH}/:slug/r-:rxGuid`;

  return (
    <ExperimentContextProvider onVariantSelected={(experimentName, variantName) => {
      tracker.register({ [experimentName]: variantName });
    }}>
      <SiftMeta />
      <Meta title={`${data.restaurantV2ByShortUrl.name} | Toast`} primaryColor="#FF4C00" icon="icons/favicon-32x32.png" domain={resources.toastLocalHost} />
      <DebugContextProvider>
        <RestaurantContextProvider ooUsage={RxDataUsage.Required} sitesUsage={RxDataUsage.None} isEditor={false}>
          <RestaurantRoutesContextProvider shortUrl={slug} homePath={homePath} orderPath={profilePath}
            orderPathPattern={profilePathPattern} checkoutPathPrefix={profilePath} confirmPathPrefix={profilePath}
            accountPath={accountPath}>
            <StructuredDataProvider>
              {children({ restaurantProfileData: data.restaurantV2ByShortUrl as RestaurantProfileRestaurantType, nearbyRxs })}
            </StructuredDataProvider>
          </RestaurantRoutesContextProvider>
        </RestaurantContextProvider>
      </DebugContextProvider>
    </ExperimentContextProvider>
  );
};

export default RestaurantProfilePageWrapper;
